import { Component, OnInit } from "@angular/core";
import * as _ from "underscore";
import { DataDictionaryService } from "../../services/data-dictionary.service";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";
import { TopLevelFieldMapping } from "../../models/top-level-field-mapping.model";
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged, filter } from "rxjs/operators";
import { MetaDataMapping } from "../../models/meta-data.model";

@Component({
  templateUrl: "./data-dictionary.component.html",
  styleUrls: ["data-dictionary.component.scss"],
  providers: [DataDictionaryService],
})
export class DataDictionaryComponent implements OnInit {
  public allSpreadsheets: { spreadsheetId: string; dataGroupName: string }[];
  public columnsToDisplay: { columnId: string; columnName: string }[];
  public groupedDetails: {
    spreadsheetId: string;
    columnId: string;
    details: { value: string; label: string }[];
  }[];

  private allTopLevelMappings: TopLevelFieldMapping[];
  public selectedSpreadsheet: {
    spreadsheetId: string;
    dataGroupName: string;
  };
  public selectedColumn: { columnId: string; columnName: string };

  public loading = false;

  public sourceFieldSearchModel = new FormControl();
  public mappingsForSourceTypeahead: MetaDataMapping[];

  constructor(
    private dataDictionaryService: DataDictionaryService,
    private loadingSpinnerService: LoadingSpinnerService
  ) {
    this.sourceFieldSearchModel.valueChanges
      .pipe(
        filter((text) => text.length > 2),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(async (value) => {
        this.mappingsForSourceTypeahead = null;
        this.mappingsForSourceTypeahead =
          await this.dataDictionaryService.getSourceFieldMappings(value);
      });
  }

  async ngOnInit() {
    this.loadingSpinnerService.start();

    this.allTopLevelMappings =
      await this.dataDictionaryService.getTopLevelDataMappings();

    // Filter out mappings with null or empty spreadsheetId
    const filteredMappings = this.allTopLevelMappings.filter(
      (mapping) =>
        mapping.topLevelSpreadsheetId && mapping.topLevelSpreadsheetId.trim()
    );

    // Get unique mappings based on spreadsheetId
    const seen = new Set();
    const uniqueMappings = filteredMappings.filter((mapping) => {
      if (seen.has(mapping.topLevelSpreadsheetId)) {
        return false;
      }
      seen.add(mapping.topLevelSpreadsheetId);
      return true;
    });

    // Create an array of objects containing spreadsheetId and dataGroupName
    this.allSpreadsheets = uniqueMappings.map((mapping) => ({
      spreadsheetId: mapping.topLevelSpreadsheetId,
      dataGroupName: mapping.topLevelDatagroupName,
    }));

    // Sort the array alphabetically based on spreadsheetId
    this.allSpreadsheets.sort((a, b) =>
      a.spreadsheetId.localeCompare(b.spreadsheetId)
    );

    this.loadingSpinnerService.stop();
  }

  public viewSpreadsheet(spreadsheet: {
    spreadsheetId: string;
    dataGroupName: string;
  }) {
    this.selectedSpreadsheet = spreadsheet;
    this.selectedColumn = null;
    this.groupedDetails = null;
    this.columnsToDisplay = null;

    // Filter for columns in selected spreadsheet
    const columns = this.allTopLevelMappings.filter(
      (mapping) => mapping.topLevelSpreadsheetId === spreadsheet.spreadsheetId
    );

    // Create an array of objects containing columnId and columnName
    this.columnsToDisplay = columns.map((column) => ({
      columnId: column.topLevelColumnId,
      columnName: column.topLevelColumnName,
    }));

    // Sort the array alphabetically based on spreadsheetId
    this.allSpreadsheets.sort((a, b) =>
      a.spreadsheetId.localeCompare(b.spreadsheetId)
    );
  }

  public async getTopLevelDetails(column: {
    columnId: string;
    columnName: string;
  }) {
    this.selectedColumn = column;
    this.groupedDetails = null;
    this.loadingSpinnerService.start();
    this.loading = true;

    const topLevelDetails = await this.dataDictionaryService.getTopLevelDetails(
      this.selectedSpreadsheet.spreadsheetId,
      column.columnId
    );

    const groupedData = topLevelDetails.reduce((acc, item) => {
      const key = `${item.spreadsheetId}_${item.columnId}`;
      if (!acc[key]) {
        acc[key] = {
          spreadsheetId: item.spreadsheetId,
          columnId: item.columnId,
          details: [],
        };
      }
      if (item.value || item.label) {
        acc[key].details.push({ value: item.value, label: item.label });
      }
      return acc;
    }, {});

    this.groupedDetails = Object.values(groupedData);
    this.loadingSpinnerService.stop();
    this.loading = false;
  }

  async gotoSourceField(sourceField: MetaDataMapping) {
    this.mappingsForSourceTypeahead = null;
    this.sourceFieldSearchModel.setValue("");
    const targetMapping = await this.dataDictionaryService.findTopLevelMapping(
      sourceField
    );

    this.viewSpreadsheet({
      spreadsheetId: targetMapping.topLevelSpreadsheetId,
      dataGroupName: targetMapping.topLevelDatagroupName,
    });

    this.getTopLevelDetails({
      columnId: targetMapping.topLevelColumnId,
      columnName: targetMapping.topLevelColumnName,
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/home/data-dictionary/data-dictionary.component.ts